import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText, Clock, CheckCircle2, XCircle } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const KIND_VALUES = ["identity", "professional", "company", "budget", "project"];

export default function Verification() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : i18n.language === "pt" ? "pt-BR" : "es-ES";
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("identity");
  const [notes, setNotes] = useState("");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const KINDS = useMemo(
    () => KIND_VALUES.map((v) => ({
      value: v,
      label: t(`verifications.kind_${v}`),
      desc: t(`verifications.kind_${v}_desc`),
    })),
    [t],
  );

  const statusLabels = useMemo(() => ({
    pending: { label: t("verifications.status_pending"), color: "bg-amber-100 text-amber-700", icon: Clock },
    approved: { label: t("verifications.status_approved"), color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    rejected: { label: t("verifications.status_rejected"), color: "bg-red-100 text-red-700", icon: XCircle },
  }), [t]);

  const load = async () => {
    const { data } = await api.get("/api/verifications/mine");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error(t("verifications.toast_max_size"));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments((d) => [...d, { id: data.id, url: apiUrl(data.url), name: data.original_filename }]);
    } catch (e) {
      toast.error(t("verifications.toast_upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (documents.length === 0) return toast.error(t("verifications.toast_docs_required"));
    try {
      await api.post("/api/verifications", { kind, notes, documents });
      setDocuments([]);
      setNotes("");
      load();
      toast.success(t("verifications.toast_submitted"));
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  return (
    <DashboardLayout
      title={t("verifications.title")}
      subtitle={t("verifications.subtitle")}
    >
      {user?.verified && (
        <div className="kpi-card p-5 bg-emerald-50 border-emerald-200 flex items-center gap-4 mb-6">
          <ShieldCheck size={24} className="text-emerald-600" />
          <div>
            <p className="text-emerald-900 font-semibold">{t("verifications.already_verified_title")}</p>
            <p className="text-emerald-700 text-sm">{t("verifications.already_verified_desc")}</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-[1fr_1.3fr] gap-6">
        <div className="kpi-card p-6">
          <h3 className="section-title mb-4">{t("verifications.new_request")}</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-600">{t("verifications.label_kind")}</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="select-verification-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">{KINDS.find((k) => k.value === kind)?.desc}</p>
            </div>
            <div>
              <Label className="text-sm text-slate-600">{t("verifications.label_notes")}</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="textarea-verification-notes" placeholder={t("verifications.notes_placeholder")} />
            </div>
            <div>
              <Label className="text-sm text-slate-600">{t("verifications.label_documents")}</Label>
              <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => upload(e.target.files?.[0])} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="button-upload-verification">
                <Upload size={14} className="mr-2" /> {uploading ? t("verifications.uploading") : t("verifications.add_document")}
              </Button>
              {documents.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {documents.map((d, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
                      <span className="inline-flex items-center gap-2"><FileText size={12} /> {d.name}</span>
                      <button onClick={() => setDocuments(documents.filter((_, j) => j !== i))} className="text-red-600 text-xs">{t("verifications.remove")}</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button onClick={submit} disabled={documents.length === 0} data-testid="button-submit-verification" className="w-full bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
              {t("verifications.submit")}
            </Button>
          </div>
        </div>

        <div>
          <h3 className="section-title mb-4">{t("verifications.history_title")}</h3>
          {items.length === 0 ? (
            <div className="kpi-card p-8 text-center text-slate-500">{t("verifications.history_empty")}</div>
          ) : (
            <div className="space-y-3">
              {items.map((v) => {
                const s = statusLabels[v.status] || { label: v.status, color: "bg-slate-100 text-slate-600", icon: Clock };
                const Icon = s.icon;
                return (
                  <div key={v.id} className="kpi-card p-5" data-testid={`verification-${v.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-slate-500">{KINDS.find((k) => k.value === v.kind)?.label || v.kind}</p>
                        <p className="text-sm text-slate-700 mt-1">{v.notes || t("verifications.no_notes")}</p>
                        {v.reason && <p className="text-xs text-red-600 mt-2">{t("verifications.reason_prefix", { reason: v.reason })}</p>}
                        <p className="text-xs text-slate-400 mt-2">{new Date(v.created_at).toLocaleString(dateLocale)}</p>
                      </div>
                      <Badge className={`${s.color} hover:${s.color}`}><Icon size={12} className="mr-1 inline" /> {s.label}</Badge>
                    </div>
                    {v.documents?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {v.documents.map((d, i) => (
                          <a key={i} href={d.url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-petroleum hover:text-gold border border-slate-200 rounded-full px-3 py-1">
                            <FileText size={10} /> {d.name}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
